import * as Sentry from '@sentry/vue';
import { useAppStore } from '@stores/app';
import { logError } from '@utils/error-tracking';

let wafIntegrationPromise: Promise<boolean> | null = null;

export function waitForAwsWafIntegration() {
	const appStore = useAppStore();

	if (appStore.wafIsLoaded) {
		return Promise.resolve(true);
	}

	if (!wafIntegrationPromise) {
		wafIntegrationPromise = new Promise<boolean>((resolve, reject) => {
			const maxAttempts = 50;
			const interval = 100;
			let attempts = 0;

			const checkAwsWafIntegration = () => {
				attempts++;
				if (window.AwsWafIntegration) {
					appStore.wafIsLoaded = true;
					Sentry.addBreadcrumb({
						category: 'waf',
						message: 'AWS WAF Integration loaded successfully',
						level: 'info',
						data: { attempts }
					});
					resolve(true);
				} else if (attempts >= maxAttempts) {
					appStore.wafIsLoaded = false;
					const error = new Error('AWS WAF Integration is not available.');
					const wafScriptElement = document.getElementById('waf-script') as HTMLScriptElement | null;
					logError(error, 'error', {
						key: 'waf-integration-timeout',
						data: {
							attempts,
							maxAttempts,
							totalWaitTime: maxAttempts * interval,
							wafScriptElement: !!wafScriptElement,
							scriptLoaded: wafScriptElement?.getAttribute('data-loaded') || null,
							scriptError: wafScriptElement?.getAttribute('data-error') || null,
							windowAwsWafExists: 'AwsWafIntegration' in window,
							documentReadyState: document.readyState
						}
					});
					reject(error);
				} else {
					const wafScriptEl = document.getElementById('waf-script') as HTMLScriptElement | null;
					if (wafScriptEl?.getAttribute('data-retries-exhausted')) {
						appStore.wafIsLoaded = false;
						const error = new Error('AWS WAF Integration is not available.');
						logError(error, 'error', {
							key: 'waf-integration-timeout',
							data: {
								attempts,
								maxAttempts,
								totalWaitTime: attempts * interval,
								wafScriptElement: true,
								scriptLoaded: null,
								scriptError: wafScriptEl.getAttribute('data-error'),
								windowAwsWafExists: 'AwsWafIntegration' in window,
								documentReadyState: document.readyState
							}
						});
						reject(error);
						return;
					}
					setTimeout(checkAwsWafIntegration, interval);
				}
			};

			checkAwsWafIntegration();
		}).finally(() => {
			wafIntegrationPromise = null;
		});
	}

	return wafIntegrationPromise;
}

export function getWafToken() {
	return waitForAwsWafIntegration().then(() => {
		return window.AwsWafIntegration.getToken();
	});
}
